import React from 'react'
import { StatusCategory } from '_gql'

import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import { columnSizing, createTagColumn, createTextColumn } from '@divvy-web/skylab.datagridnext'
import { createColumnHelper } from '@divvy-web/skylab.tanstack-react-table'

import ViewDetailsButton from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/ViewDetailsButton'
import { formatDate, getDashboardStatusTagProps } from '@sputnik/pages/DashboardPages/utils'

import { GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge } from './GetCreditApplications.gql'

const fillColumnSizing = columnSizing.fill(200)

type CreditApplicationEdge =
  GetCreditApplications_creditApplicationsPaginated_CreditApplicationPaginatedConnection_edges_CreditApplicationPaginatedEdge

const columnHelper = createColumnHelper<CreditApplicationEdge>()

// TODO: figure out if this mapping contains all statuses that need mapped

interface ColumnConfigurationParams {
  onViewDetailsClick: (salesforceCreditId: string) => void
}

export const getColumnConfiguration = ({ onViewDetailsClick }: ColumnConfigurationParams) => [
  createTextColumn(columnHelper, 'node.businessInfo.legalBusinessName', {
    id: 'business-name',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Company name'
        id='sputnik.dataGrid__FPGwAt'
      />
    ),
    ...fillColumnSizing,
  }),
  createTagColumn(columnHelper, 'node.statusCategory', {
    id: 'status',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Status'
        id='sputnik.dataGrid__tzMNF3'
      />
    ),
    options: {
      [StatusCategory.IN_REVIEW]: getDashboardStatusTagProps(StatusCategory.IN_REVIEW),
      [StatusCategory.INCOMPLETE]: getDashboardStatusTagProps(StatusCategory.INCOMPLETE),
      [StatusCategory.EXPIRES]: getDashboardStatusTagProps(StatusCategory.EXPIRES),
      [StatusCategory.ACTION_NEEDED]: getDashboardStatusTagProps(StatusCategory.ACTION_NEEDED),
      [StatusCategory.ACCOUNT_CREATED]: getDashboardStatusTagProps(StatusCategory.ACCOUNT_CREATED),
      [StatusCategory.DECLINED]: getDashboardStatusTagProps(StatusCategory.DECLINED),
    },
    ...fillColumnSizing,
  }),
  createTextColumn(columnHelper, ({ node }) => formatDate(node?.startedAt), {
    id: 'started-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Started on'
        id='sputnik.dataGrid__Z5cU8c'
      />
    ),
    ...fillColumnSizing,
  }),
  createTextColumn(columnHelper, ({ node }) => formatDate(node?.expiresAt), {
    id: 'expires-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Expires on'
        id='sputnik.dataGrid__0t4WRs'
      />
    ),
    ...fillColumnSizing,
  }),
  createTextColumn(
    columnHelper,
    ({ node }) => (!node?.fundableLimit ? '—' : <FormattedCurrency value={node.fundableLimit} />),
    {
      id: 'line-of-credit',
      enableSorting: true,
      header: () => (
        <FormattedMessage
          defaultMessage='Line of credit'
          id='sputnik.dataGrid__uYh9fa'
        />
      ),
      meta: {
        justify: 'right',
      },
      ...fillColumnSizing,
    },
  ),
  columnHelper.display({
    id: 'action',
    header: () => (
      <FormattedMessage
        defaultMessage='Action'
        id='sputnik.columns__QlsDcr'
      />
    ),
    cell: ({ row }) => (
      <ViewDetailsButton
        salesforceCreditId={row.original.node?.salesforceCreditId ?? ''}
        onClick={() => onViewDetailsClick(row.original.node?.salesforceCreditId ?? '')}
      />
    ),
    meta: {
      justify: 'right',
    },
    ...fillColumnSizing,
  }),
]
